//
//  trialConfigurationNavigation.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module defines trial Configuration Navigation routing and navigation data.
//

import { Permission } from "src/api/Tmd.dtos";
import AppSettings from "src/AppSettings";
import { LinkPermissionMode, type ILayoutSubMenu } from "src/contexts/layouts/LayoutContext";

import {
    toSettingsNavigationLayoutLink,
    type ISettingsNavigationDestination
} from "./settingsNavigation";

/**
 * Builds trial configuration destinations with their existing cards and permission requirements.
 *
 * Postconditions: Returns a fresh ordered list. The numeric trial ID is interpolated without
 * validation; consumers perform permission filtering and navigation.
 */
export const createTrialConfigurationDestinations = (
    trialId: number
): ISettingsNavigationDestination[] => {
    const basePath = `/trials/${trialId}/configuration`;

    return [
        {
            label: "QA Program Checklist",
            href: `${basePath}/qa-program-checklist`,
            permissionMode: LinkPermissionMode.Any,
            permissions: [
                Permission.TrialQaProgramChecklistView,
                Permission.TrialQaProgramChecklistCreate,
                Permission.TrialQaProgramChecklistUpdate
            ],
            card: {
                description:
                    "Initialize and maintain this trial's independent QA program checklist structure.",
                icon: AppSettings.icons.models.qaProgramChecklist
            }
        },
        {
            label: "Institution Capabilities",
            href: `${basePath}/capabilities`,
            permissionMode: LinkPermissionMode.Any,
            permissions: [Permission.InstitutionCapabilitiesView],
            card: {
                description:
                    "Choose the capabilities permitted by this trial and mark them as required or optional.",
                icon: AppSettings.icons.models.trialCapability
            }
        },
        {
            label: "Emails",
            href: `${basePath}/emails`,
            permissionMode: LinkPermissionMode.Any,
            permissions: [Permission.TrialEmailRecipientsView],
            card: {
                description: "Set the default email recipients for every site on this trial.",
                icon: AppSettings.icons.models.emailRecipientConfiguration
            }
        },
        {
            label: "Personnel Email Preferences",
            href: `${basePath}/emails/preferences`,
            permissionMode: LinkPermissionMode.Any,
            permissions: [Permission.TrialPersonnelEmailPreferencesView],
            card: {
                description:
                    "See and manage where the people on this trial have chosen their email to go.",
                icon: AppSettings.icons.models.personnelEmailPreference
            }
        },
        {
            label: "Institution Tags",
            href: `${basePath}/tags`,
            permissionMode: LinkPermissionMode.Any,
            permissions: [Permission.TrialUpdate],
            card: {
                description: "Choose the institution tags available within this trial.",
                icon: AppSettings.icons.models.trialTag
            }
        },
        {
            label: "Participant Tags",
            href: `${basePath}/participant-tags`,
            permissionMode: LinkPermissionMode.Any,
            permissions: [Permission.TrialPatientTagSelectionsView],
            card: {
                description: "Choose the participant tags available within this trial.",
                icon: AppSettings.icons.models.patientTag
            }
        },
        {
            label: "Trial Group Item Templates",
            href: `${basePath}/group-item-templates`,
            permissionMode: LinkPermissionMode.Any,
            permissions: [Permission.TrialGroupView],
            card: {
                description: "Configure Group Items for Groups linked to this trial.",
                icon: AppSettings.icons.models.item
            }
        },
        {
            label: "Institution Item Templates",
            href: `${basePath}/institution-item-templates`,
            permissionMode: LinkPermissionMode.Any,
            permissions: [Permission.TrialInstitutionItemView],
            card: {
                description:
                    "Configure and distribute the item templates used by this trial's institutions.",
                icon: AppSettings.icons.models.trialInstitutionItemTemplate
            }
        },
        {
            label: "Participant Item Templates",
            href: `${basePath}/participant-item-templates`,
            permissionMode: LinkPermissionMode.Any,
            permissions: [Permission.TrialPatientItemTemplatesView],
            card: {
                description: "Configure and distribute item templates used by participants.",
                icon: AppSettings.icons.models.patientItemTemplate
            }
        },
        {
            label: "API",
            href: `${basePath}/api`,
            permissionMode: LinkPermissionMode.Any,
            permissions: [Permission.TrialApisView],
            card: {
                description: "Manage this trial's integration and API settings.",
                icon: AppSettings.icons.models.trialApi
            }
        }
    ];
};

/**
 * Collects distinct configuration permissions in first-seen order using ID zero only for metadata.
 */
export const trialConfigurationPermissions = [
    ...new Set(createTrialConfigurationDestinations(0).flatMap(({ permissions }) => permissions))
];

/**
 * Builds a trial-context submenu from the configuration destinations using the shared projection.
 */
export const createTrialConfigurationSubMenu = (trialId: number): ILayoutSubMenu => ({
    code: "trialConfiguration",
    context: { trialId },
    items: createTrialConfigurationDestinations(trialId).map(toSettingsNavigationLayoutLink)
});

/**
 * Builds the Configuration section with shared permissions and fresh trial-specific children.
 */
export const createTrialConfigurationSection = (
    trialId: number
): ISettingsNavigationDestination => ({
    label: "Configuration",
    title: "Trial Configuration",
    href: `/trials/${trialId}/configuration`,
    description: "Manage settings that apply only to this trial.",
    permissionMode: LinkPermissionMode.Any,
    permissions: trialConfigurationPermissions,
    card: {
        description: "Manage settings that apply only to this trial.",
        icon: AppSettings.icons.settings
    },
    children: createTrialConfigurationDestinations(trialId)
});
